import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PaymentPlanHistoryItem } from '@frontend/api-payment-plans';
import { PrintDialog } from '@frontend/assets';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { downloadCSV } from '@frontend/media-helpers';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useParams, useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Modal, Table, Text, useModalControl, useAlert, Button } from '@frontend/design-system';
import { useCanDoAction } from '../../../hooks/use-can-do-action';
import { usePrintDialogue } from '../../../hooks/use-print-dialogue';
import { useTogglePaymentPlanRecorded } from '../../../hooks/use-toggle-payment-plan-recorded';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { prettify } from '../../../utils/date-helpers';
import { ViewPaymentDetailsModal } from '../../PaymentPlanModals/ViewPaymentDetailsModal';
import { PrintHeader } from '../../PrintHeader';
import { SmallTableView } from '../../SmallTableView/small-table-view';
import { getSmallViewTableColumns } from '../../SmallTableView/utils';
import { BalancePaid } from './atoms/BalancePaid';
import { PaymentPlanHistoryStyleWrapper } from './atoms/PaymentPlanHistoryStyleWrapper';
import { generateColumns } from './generate-columns';
import { getPaymentsPlanHistoryActions } from './get-payments-plan-history-actions';
import { UpdateCustomRow } from './UpdateCustomRow';

export const PaymentPlanHistory = ({
  locationId,
  planHistory,
  balancePaid,
  paymentPlanId,
  showPrint = false,
}: {
  locationId: string | undefined;
  planHistory: PaymentPlanHistoryItem[];
  balancePaid: number | undefined;
  paymentPlanId: string;
  showPrint: boolean;
}) => {
  const { t } = useTranslation('payments');
  const columns = useMemo(() => generateColumns, []);
  const alert = useAlert();
  const params = useParams({ from: '/payments/payment-plans_/$planId', shouldThrow: false });
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });
  const { canExport } = useCanDoAction(locationId);
  const [selectedPayment, setSelectedPayment] = useState<PaymentPlanHistoryItem>();
  const { modalProps: viewPaymentModalProps, triggerProps: viewPaymentTriggerProps } = useModalControl();
  const {
    showPrint: showTablePrint,
    setShowPrint: setShowTablePrint,
    handlePrint: handleTablePrint,
  } = usePrintDialogue();
  const { toggleRecorded, isSuccess } = useTogglePaymentPlanRecorded({ paymentPlanId, locationId });
  const navigate = useNavigate();

  useEffect(() => {
    if (isSuccess) {
      setSelectedPayment(planHistory.find((item) => item?.payment?.id === selectedPayment?.payment?.id));
    }
  }, [isSuccess]);

  const transformDataForCsvExport = (data: PaymentPlanHistoryItem[]) => {
    return data.map((historyItem) => {
      let type;
      let date;
      let amount;
      let status;
      let balance;
      let remaining;
      if (historyItem.type === 'planUpdated') {
        type = prettify(historyItem.type ?? '');
        date = formatDate(historyItem?.occurredAt, 'YYYY-MM-D');
        amount = formatCentsToCurrency(historyItem.plan?.to?.schedule.amount)?.replace(',', '') || '0.00';
        status = prettify(historyItem.plan?.to?.status || '', {
          split: '_',
          outputCase: 'Sentence case',
        });
        balance =
          formatCentsToCurrency(historyItem?.plan?.to?.paymentSummary.remainingAmount)?.replace(',', '') || '0.00';
        remaining = historyItem?.plan?.to?.paymentSummary.remainingTerms || 0;
      } else {
        type = prettify(historyItem.type ?? '');
        date = formatDate(historyItem?.occurredAt, 'YYYY-MM-D');
        amount = formatCentsToCurrency(historyItem?.payment?.amount)?.replace(',', '') || '0.00';
        status = prettify(historyItem?.payment?.status || '', {
          split: '_',
          outputCase: 'Sentence case',
        });
        balance = formatCentsToCurrency(historyItem?.payment?.balanceRemaining)?.replace(',', '') || '0.00';
        remaining = historyItem?.payment?.remainingTerm || 0;
      }

      return {
        Type: type,
        Date: date,
        Amount: amount,
        'Payment Status': status,
        Balance: balance,
        Remaining: remaining,
      };
    });
  };

  const exportTransactions = () => {
    if (!canExport) {
      alert.error(t('Payment admin role required'));
      return;
    }

    if (planHistory && planHistory.length > 0) {
      const filename = `plan_payment_history_${params?.planId}`;
      const transformedData = transformDataForCsvExport(
        planHistory?.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
      );

      downloadCSV(transformedData, filename);
    }
  };

  return (
    <PaymentPlanHistoryStyleWrapper isMobile={isMobile}>
      <div
        css={css`
          display: flex;
          flex-flow: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <Text
          weight='bold'
          css={css`
            font-size: ${theme.fontSize(18)};
            margin: ${theme.spacing(4, 0, 2)};
          `}
        >
          {t('Payment History')}
        </Text>
        <div
          css={css`
            display: flex;
            flex-flow: row;
          `}
        >
          <Button
            size='large'
            variant='secondary'
            iconName='print'
            trackingId='pay-portal-paymentplan-btn-print'
            hoverLabel={t('Print')}
            onClick={handleTablePrint}
          />
          <Button
            size='large'
            variant='secondary'
            iconName='download'
            trackingId='pay-portal-paymentplan-btn-download'
            hoverLabel={t('Download')}
            onClick={exportTransactions}
          />
        </div>
      </div>
      <div
        css={css`
          display: flex;
          flex-flow: row;
          gap: ${theme.spacing(2)};
          ${!isMobile &&
          `
            p {
              font-weight: 600;
              font-size: ${theme.fontSize(14)};
            }
          `}
          ${isMobile &&
          `
            padding-bottom: ${theme.spacing(2)};
          `}
        `}
      >
        <BalancePaid isMobile={isMobile} balancePaid={balancePaid} />
      </div>
      {showPrint ? (
        <Table
          globalTrackingId='pay-portal-paymentplan-printtable'
          colConfig={columns}
          tableStyle={css`
            width: 100%;
          `}
          data={planHistory?.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))}
          emptyStateConfig={{
            type: 'payments',
            header: t('No data to display'),
          }}
          customRowConfig={{
            isSticky: true,
            condition: (_, rowData) => rowData.type === 'planUpdated',
            render: (_, rowData) => <UpdateCustomRow data={rowData} />,
            wrapperStyle: css`
              background: ${theme.colors.neutral5};
            `,
          }}
        />
      ) : (
        <>
          {!isMobile ? (
            <Table
              globalTrackingId='pay-portal-paymentplan-edittable'
              colConfig={columns}
              tableStyle={css`
                width: 100%;
              `}
              data={planHistory?.sort((a: any, b: any) => b.occurredAt.localeCompare(a.occurredAt)) || []}
              emptyStateConfig={{
                type: 'payments',
                header: t('No data to display'),
              }}
              customRowConfig={{
                isSticky: true,
                condition: (_, rowData) => rowData.type === 'planUpdated',
                render: (_, rowData) => <UpdateCustomRow data={rowData} />,
                wrapperStyle: css`
                  background: ${theme.colors.neutral5};
                `,
              }}
              rowActions={{
                onRowClick: (payment: PaymentPlanHistoryItem) => {
                  setSelectedPayment(payment);
                  viewPaymentTriggerProps.onClick();
                },
                actions: getPaymentsPlanHistoryActions(
                  t,
                  setSelectedPayment,
                  viewPaymentTriggerProps,
                  navigate,
                  toggleRecorded
                ),
              }}
            />
          ) : (
            <SmallTableView
              data={planHistory?.sort((a: any, b: any) => b.occurredAt.localeCompare(a.occurredAt)) || []}
              colConfig={getSmallViewTableColumns<PaymentPlanHistoryItem>(columns)}
              actions={getPaymentsPlanHistoryActions(
                t,
                setSelectedPayment,
                viewPaymentTriggerProps,
                navigate,
                toggleRecorded
              )}
              customRowConfig={{
                condition: (rowData) => rowData.type === 'planUpdated',
                render: (rowData) => <UpdateCustomRow data={rowData} />,
              }}
            />
          )}
        </>
      )}
      <Modal {...viewPaymentModalProps} maxWidth={600}>
        <ViewPaymentDetailsModal
          paymentDetails={selectedPayment as PaymentPlanHistoryItem}
          onClose={viewPaymentModalProps.onClose}
          toggleRecorded={(data, recordedAt, paymentId) =>
            toggleRecorded({
              data,
              recordedAt,
              paymentId,
            })
          }
          isRecorded={!!selectedPayment?.payment?.recordedAt}
        />
      </Modal>
      {showTablePrint && <PrintFriendly onClose={() => setShowTablePrint(false)} planHistory={planHistory} />}
    </PaymentPlanHistoryStyleWrapper>
  );
};

const PrintFriendly = ({ onClose, planHistory }: { onClose: () => void; planHistory: PaymentPlanHistoryItem[] }) => {
  const columns = useMemo(() => generateColumns, []);
  const { t } = useTranslation('payments');

  return (
    <PrintDialog show={true} onClose={onClose} showBackgroundGraphics>
      <div
        css={css`
          margin: ${theme.spacing(2)};
        `}
      >
        <PrintHeader />
        <Table
          colConfig={columns}
          tableStyle={css`
            width: 100%;
          `}
          data={planHistory}
          emptyStateConfig={{
            type: 'payments',
            header: t('No data to display'),
          }}
          customRowConfig={{
            isSticky: true,
            condition: (_, rowData) => rowData.type === 'planUpdated',
            render: (_, rowData) => <UpdateCustomRow data={rowData} />,
            wrapperStyle: css`
              background: ${theme.colors.neutral5};
            `,
          }}
        />
      </div>
    </PrintDialog>
  );
};
